import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Activity, UserCog, KeyRound, BarChart3, Settings, Power, ChevronRight, ChevronLeft, ClipboardList, GraduationCap } from "lucide-react";
import { authFetch } from "../utils/auth";
import { setStatusFavicon, resetFavicon } from "../utils/favicon";

const STATUS_POLL_INTERVAL = 30000;
// Module-level (not state) so it survives this component remounting on every
// admin-to-admin navigation, instead of resetting on each page switch.
let lastKnownStatus = null;

const NAV_ITEMS = [
  { path: "/admin/dashboard", icon: <Activity size={18} />, label: "Infrastructure" },
  { path: "/admin",           icon: <ClipboardList size={18} />, label: "Pending Approvals", exact: true },
  { path: "/admin/users",     icon: <UserCog size={18} />,  label: "User Management" },
  { path: "/admin/api-keys",  icon: <KeyRound size={18} />, label: "API Keys" },
  { path: "/admin/token-usage", icon: <BarChart3 size={18} />, label: "Token Usage" },
  // One entry for all learning pages; it opens on the Learning Catalog (patterns are learned on submit, nothing waits for review).
  { path: "/learning/catalog", activePath: "/learning", icon: <GraduationCap size={18} />, label: "Learning", prefix: true },
  { path: "/admin/settings",  icon: <Settings size={18} />, label: "Settings" },
];

const AdminSideBar = ({ onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [expanded, setExpanded] = useState(() => localStorage.getItem("adminSidebarExpanded") === "true");

  const toggleExpanded = (v) => {
    const next = typeof v === "boolean" ? v : !expanded;
    setExpanded(next);
    localStorage.setItem("adminSidebarExpanded", next);
  };

  const isActive = (item) =>
    item.exact
      ? location.pathname === item.path
      : item.prefix
        ? location.pathname === (item.activePath || item.path) || location.pathname.startsWith((item.activePath || item.path) + "/")
        : location.pathname === item.path;

  // Status-colored favicon — rendered on every admin page (this sidebar is
  // shared by all of them), so it's consistent across the whole admin area.
  // Reset back to the plain brand icon the moment the admin area is left
  // (logout, or navigating away) so no other screen inherits a stale color.
  useEffect(() => {
    let cancelled = false;
    // Each admin page is a separate top-level route, so this sidebar fully
    // remounts on every admin-to-admin navigation. Re-apply the last known
    // status immediately so the icon doesn't sit at default for the 2-3s it
    // takes the backend to re-run its live health checks; the poll below
    // still re-validates and corrects it shortly after.
    if (lastKnownStatus) setStatusFavicon(lastKnownStatus);
    const poll = async () => {
      try {
        const res = await authFetch("/api/dashboard/status/default");
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (!cancelled && data?.status) {
          lastKnownStatus = data.status;
          setStatusFavicon(data.status);
        }
      } catch {
        // network hiccup — leave whatever favicon was last set
      }
    };
    poll();
    const interval = setInterval(poll, STATUS_POLL_INTERVAL);
    return () => {
      cancelled = true;
      clearInterval(interval);
      resetFavicon();
    };
  }, []);

  return (
    <aside
      className="flex flex-col justify-between bg-[#f6f6f1] border-r border-[#EBECE6] h-screen py-4 transition-all duration-300 flex-shrink-0"
      style={{ width: expanded ? "220px" : "72px" }}
    >
      {/* Top: logo + nav */}
      <div className="flex flex-col gap-1">
        {/* Logo row + toggle */}
        <div className={`flex items-center mb-3 px-4 ${expanded ? "justify-between" : "justify-center"}`}>
          <img src="/A_bgless.png" alt="Arta" style={{ width: "36px", height: "auto", flexShrink: 0 }} />
          {expanded && (
            <span className="text-sm font-bold text-[#253439] tracking-wide ml-2 flex-1">Admin</span>
          )}
          <button
            onClick={toggleExpanded}
            className="w-7 h-7 flex items-center justify-center rounded-full border border-[#EBECE6] bg-white text-gray-400 hover:text-cyan-600 hover:border-cyan-300 transition flex-shrink-0"
            title={expanded ? "Collapse sidebar" : "Expand sidebar"}
          >
            {expanded ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
          </button>
        </div>

        {/* Nav items */}
        {NAV_ITEMS.map((item) => {
          const active = isActive(item);
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              title={!expanded ? item.label : undefined}
              className={`flex items-center gap-3 transition rounded-lg mx-2 ${
                expanded ? "px-3 py-2.5 w-auto" : "w-10 h-10 justify-center mx-auto"
              } ${
                active
                  ? "bg-cyan-500 text-white"
                  : "text-gray-500 hover:bg-cyan-50 hover:text-cyan-700"
              }`}
            >
              <span className="relative flex-shrink-0">{item.icon}</span>
              {expanded && (
                <span className="text-sm font-medium whitespace-nowrap">{item.label}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Bottom: logout */}
      <div className={`flex ${expanded ? "px-2" : "justify-center"}`}>
        <button
          onClick={onLogout}
          title="Logout"
          className={`flex items-center gap-3 text-red-500 hover:bg-red-50 rounded-lg transition ${
            expanded ? "px-3 py-2.5 w-full" : "w-10 h-10 justify-center"
          }`}
        >
          <Power size={18} className="flex-shrink-0" />
          {expanded && <span className="text-sm font-medium whitespace-nowrap">Logout</span>}
        </button>
      </div>
    </aside>
  );
};

export default AdminSideBar;
